import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** Prev / "Page x of y" / next instead of the numbered pager (mobile layout). */
  compact: boolean;
  /** Builds `${prefix}-page-prev`, `${prefix}-page-next`, `${prefix}-page-${n}` test ids. */
  testIdPrefix: string;
  className?: string;
}

const VISIBLE_PAGES = 5;

/** Page pager: numbered button group or compact prev / label / next. Renders nothing for a single page. */
export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  onChange,
  compact,
  testIdPrefix,
  className,
}) => {
  const { t } = useTranslation('common');
  if (totalPages <= 1) return null;

  const pageLabel = t('common:pageOf', { page, total: totalPages });
  const rootClass = (base: string) => (className ? `${base} ${className}` : base);

  if (compact) {
    return (
      <nav className={rootClass(styles.compact)} aria-label={pageLabel}>
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className={`${styles.pageBtn} ${styles.standalone}`}
          data-testid={`${testIdPrefix}-page-prev`}
        >
          {t('common:previous')}
        </button>
        <span className={styles.pageLabel} aria-live="polite">{pageLabel}</span>
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className={`${styles.pageBtn} ${styles.standalone}`}
          data-testid={`${testIdPrefix}-page-next`}
        >
          {t('common:next')}
        </button>
      </nav>
    );
  }

  const firstVisible = Math.max(1, Math.min(totalPages - (VISIBLE_PAGES - 1), page - 2));

  return (
    <div className={rootClass(styles.numbered)}>
      <div className={styles.pageInfo} aria-live="polite">{pageLabel}</div>
      <nav className={styles.pageNav} aria-label={pageLabel}>
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className={`${styles.pageBtn} ${styles.pageArrow}`}
          aria-label={t('common:previousPage')}
          data-testid={`${testIdPrefix}-page-prev`}
        >
          ←
        </button>
        {Array.from({ length: Math.min(VISIBLE_PAGES, totalPages) }, (_, i) => {
          const pageNum = firstVisible + i;
          const isCurrent = page === pageNum;
          return (
            <button
              type="button"
              key={pageNum}
              onClick={() => onChange(pageNum)}
              className={`${styles.pageBtn}${isCurrent ? ` ${styles.current}` : ''}`}
              aria-current={isCurrent ? 'page' : undefined}
              data-testid={`${testIdPrefix}-page-${pageNum}`}
            >
              {pageNum}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className={`${styles.pageBtn} ${styles.pageArrow}`}
          aria-label={t('common:nextPage')}
          data-testid={`${testIdPrefix}-page-next`}
        >
          →
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
